import React, { useEffect, useRef } from 'react';
import styles from '../../styles/contacts/ContactsWorkspace.module.css';
import {
  CopyIcon,
  DownloadIcon,
  FlagIcon,
  MailIcon,
  PhoneIcon,
  SendIcon,
  ShareIcon,
  TrashIcon,
  UndoIcon,
  UsersIcon,
} from './ContactsIcons';
import { canRetractCard, contactDisplayName } from './contactsData';

/**
 * Per-contact action menu. Rendered as an anchored popover on pointer devices
 * and as a bottom sheet on touch, where it is also what a long press opens.
 */
export default function ContactMenu({ contact, variant = 'popover', dropUp = false, onClose, actions }) {
  const containerRef = useRef(null);
  // Callers pass an inline handler; a ref keeps the document listeners from
  // being torn down and re-added on every render of the list.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
      }
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown, true);
    // Capture phase: the row underneath would otherwise swallow the click.
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, []);

  useEffect(() => {
    const first = containerRef.current?.querySelector('button, a');
    first?.focus?.({ preventScroll: true });
  }, []);

  if (!contact) return null;

  const run = (fn) => () => {
    onClose?.();
    fn?.(contact);
  };

  // The popover is a real ARIA menu, so its entries are menu items reached
  // with the arrow keys. The sheet is a dialog, where plain buttons are right.
  const isMenu = variant !== 'sheet';
  const itemProps = isMenu ? { role: 'menuitem', tabIndex: -1 } : {};

  /** Roving focus across the menu items. */
  const onMenuKeyDown = (event) => {
    if (!isMenu) return;
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const items = Array.from(containerRef.current?.querySelectorAll('[role="menuitem"]') || []);
    if (items.length === 0) return;
    event.preventDefault();
    // The list behind the menu also moves its selection on the arrow keys.
    event.stopPropagation();
    const current = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % items.length;
    else next = current <= 0 ? items.length - 1 : current - 1;
    items[next]?.focus?.();
  };

  const items = (
    <>
      {contact.email ? (
        <a
          className={styles.menuItem}
          {...itemProps}
          href={`mailto:${contact.email}`}
          onClick={() => {
            onClose?.();
            actions.onEmail?.(contact);
          }}
        >
          <MailIcon size={16} />
          Send an email
        </a>
      ) : null}
      {contact.phone ? (
        <a
          className={styles.menuItem}
          {...itemProps}
          href={`tel:${contact.phone}`}
          onClick={() => {
            onClose?.();
            actions.onCall?.(contact);
          }}
        >
          <PhoneIcon size={16} />
          Call
        </a>
      ) : null}
      {contact.email ? (
        <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onCopyEmail)}>
          <CopyIcon size={16} />
          Copy email address
        </button>
      ) : null}

      <div className={styles.menuSep} role="presentation" />

      <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onSaveVcf)}>
        <DownloadIcon size={16} />
        Save to phone
      </button>
      <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onShare)}>
        <ShareIcon size={16} />
        Share contact
      </button>
      <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onToggleFlag)}>
        <FlagIcon size={16} filled={!contact.isFlagged} />
        {contact.isFlagged ? 'Remove flag' : 'Flag for follow-up'}
      </button>

      <div className={styles.menuSep} role="presentation" />

      {canRetractCard(contact) ? (
        <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onRetractCard)}>
          <UndoIcon size={16} />
          Retract my card
        </button>
      ) : (
        <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onSendCard)}>
          <SendIcon size={16} />
          Send my card
        </button>
      )}
      {variant === 'sheet' ? (
        <button type="button" className={styles.menuItem} {...itemProps} onClick={run(actions.onOpen)}>
          <UsersIcon size={16} />
          Open full profile
        </button>
      ) : null}

      <div className={styles.menuSep} role="presentation" />

      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuItemDanger}`}
        {...itemProps}
        onClick={run(actions.onDelete)}
      >
        <TrashIcon size={16} />
        Remove contact
      </button>
    </>
  );

  if (variant === 'sheet') {
    return (
      <div className={styles.menuSheetOverlay} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div
          ref={containerRef}
          className={styles.menuSheet}
          role="dialog"
          aria-modal="true"
          aria-label={`Actions for ${contactDisplayName(contact)}`}
        >
          <div className={styles.menuSheetTitle}>{contactDisplayName(contact)}</div>
          {items}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.menu} ${dropUp ? styles.menuUp : ''}`}
      role="menu"
      aria-label={`Actions for ${contactDisplayName(contact)}`}
      onKeyDown={onMenuKeyDown}
    >
      {items}
    </div>
  );
}
